import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import {
  BarChart3, TrendingUp, Gauge, Sparkles, Target, ShieldCheck,
  ArrowRight, Loader2, Check, X, AlertTriangle, Brain, FileText,
  Clock, MapPin, Euro, Percent, Copy, CheckCircle2, Lightbulb,
  Archive, Swords, Plus, Search, Tag, Trash2, Edit, Wand2, BookOpen, Trophy,
  Building2, BookMarked, Users, Signal, ClipboardCheck, PhoneCall
} from "lucide-react";
import { toast } from "sonner";
import { ESTADO_LABELS, ESTADO_CLS, fmtEur, Field, Kpi, fmtFechaOferta } from "./shared";

export default function PipelineView() {
  const [data, setData] = useState(null);
  const [cartera, setCartera] = useState(null);
  const [minMm, setMinMm] = useState(0); // Iter283 · filtro de importe persistente
  const [showPonderado, setShowPonderado] = useState(true); // Entrega B · capa opcional
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/tender360/pipeline`)
      .then(r => setData(r.data))
      .catch((e) => toast.error("Pipeline: " + (e.response?.data?.detail || e.message)));
    axios.get(`${API}/tender360/cartera-operativa`)
      .then(r => setCartera(r.data))
      .catch(() => {}); // sección adicional informativa — si falla no bloquea el resto del panel
  }, []);

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  const { kpis, stages, live_stage_keys, previsiones_vencidas, reconciliacion } = data;
  const liveStages = stages.filter(s => live_stage_keys.includes(s.key));
  const maxStage = Math.max(...liveStages.map(s => s.importe_total), 1);
  const forecastRows = liveStages.flatMap(s => s.items.map(it => ({ ...it, stage: s.label })))
    .filter(it => (it.importe || 0) >= minMm * 1_000_000 && !it.prevision_vencida)
    .sort((a, b) => b.importe_ponderado - a.importe_ponderado);

  // N06 (roadmap-valoriza-jev-2026-10-11, Ola 3) · "no se describe un 0% con
  // n=0 como mal desempeño ni un 1/1 como patrón estable" — el win rate no
  // se presenta como una tasa fiable si la muestra cerrada es insuficiente.
  const winRateDisplay = kpis.win_rate_pct == null
    ? "N/D"
    : kpis.win_rate_muestra_suficiente ? `${kpis.win_rate_pct}%` : `${kpis.win_rate_pct}% (muestra baja)`;

  const kpiCards = [
    { label: "Pipeline en curso",       val: fmtEur(kpis.total_pipeline_raw_eur),     sub: `${kpis.en_curso} oportunidades` },
    ...(showPonderado ? [{ label: "Forecast ponderado", val: fmtEur(kpis.total_pipeline_weighted_eur), sub: "Importe × probabilidad (capa opcional)" }] : []),
    { label: "Win rate",                val: winRateDisplay,  sub: `${kpis.adjudicadas} adj. / ${kpis.desestimadas} des. (n=${kpis.win_rate_muestra_cerrada})` },
    { label: "Total oportunidades",     val: kpis.total_licitaciones,                  sub: "en la fuente comercial" },
  ];

  return (
    <div className="space-y-4" data-testid="pipeline-view">
      {showPonderado && kpis.sin_probabilidad_revisada_count > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex items-center gap-2" data-testid="pipeline-sin-revisar-banner">
          <AlertTriangle size={14} />
          <span><b>{kpis.sin_probabilidad_revisada_count}</b> de {kpis.total_licitaciones} oportunidades no tienen una probabilidad de éxito confirmada por un analista ({fmtEur(kpis.sin_probabilidad_revisada_importe_eur)}). El forecast ponderado usa el valor por etapa como <b>hipótesis</b>, no una estimación real.</span>
        </div>
      )}
      {kpis.previsiones_vencidas_count > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-700 flex items-center gap-2" data-testid="pipeline-vencidas-banner">
          <Clock size={14} />
          <span><b>{kpis.previsiones_vencidas_count}</b> previsiones con fecha de adjudicación estimada ya pasada ({fmtEur(kpis.previsiones_vencidas_importe_eur)}) se excluyen del forecast vigente hasta revisar la fecha o el estado real — ver &quot;Previsiones vencidas&quot; abajo.</span>
        </div>
      )}
      {/* KPIs · fuente única (Entrega B) + toggle de capa ponderada */}
      <div className="flex items-center justify-between gap-2 flex-wrap -mb-1">
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
          <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">Fuente única: ofertas_comercial</span>
          <span>mismo dato que Licitaciones comercial y YTD/Business Review. El forecast ponderado es una capa opcional.</span>
        </div>
        <button
          onClick={() => setShowPonderado(v => !v)}
          data-testid="pipeline-toggle-ponderado"
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-lg px-2.5 py-1 border transition ${showPonderado ? "border-[#007934]/30 bg-[#007934]/5 text-[#007934]" : "border-gray-200 bg-white text-gray-500 hover:border-gray-400"}`}
          title="Muestra u oculta el forecast ponderado (importe × probabilidad)">
          <Percent size={12} /> Forecast ponderado: {showPonderado ? "ON" : "OFF"}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {kpiCards.map(k => (
          <div key={k.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide">{k.label}</div>
            <div className="text-xl font-bold text-gray-900 mt-1">{k.val}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Stage funnel */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Funnel de pipeline</h3>
        {liveStages.map(s => {
          const pct = maxStage > 0 ? (s.importe_total / maxStage) * 100 : 0;
          return (
            <div key={s.key} data-testid={`stage-${s.key}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <div className="font-semibold text-gray-800">{s.label}</div>
                <div className="flex items-center gap-3 text-gray-500">
                  <span>{s.count} ops.</span>
                  <span className="font-mono">{fmtEur(s.importe_total)}</span>
                  {showPonderado && <span className="font-mono text-[#007934] font-semibold">Pond.: {fmtEur(s.importe_ponderado)}</span>}
                </div>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden relative">
                <div className="bg-gradient-to-r from-[#007934] to-[#00b050] h-full rounded-full flex items-center px-2"
                     style={{ width: `${Math.max(pct, 3)}%` }}>
                  {s.items.slice(0, 2).map(it => (
                    <span key={it.id} className="text-[9px] text-white truncate mr-2">{it.municipio}</span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail list of weighted opportunities */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Oportunidades en el forecast</span>
          {/* Iter283 · Importe ≥ X M€ — filtro persistente pedido por el usuario */}
          <div
            data-testid="t360-filter-importe-wrap"
            title="Filtra oportunidades con importe por encima del umbral"
            className={`inline-flex items-center gap-1.5 border rounded-lg px-2 py-1 text-[11px] ${
              minMm > 0 ? "border-sky-300 bg-sky-50" : "border-gray-200 bg-white"
            }`}>
            <span className={minMm > 0 ? "font-semibold text-sky-700" : "text-gray-500"}>Importe ≥</span>
            <input
              type="range" min={0} max={30} step={1}
              value={minMm}
              onChange={(e) => setMinMm(Number(e.target.value))}
              data-testid="t360-filter-importe"
              className="accent-sky-500 w-20"
            />
            <span className={`font-mono w-12 text-right ${minMm > 0 ? "text-sky-700 font-bold" : "text-gray-500"}`}>{minMm}M€</span>
          </div>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left p-2.5">Etapa</th>
              <th className="text-left p-2.5">Municipio</th>
              <th className="text-left p-2.5">Licitación</th>
              <th className="text-right p-2.5">Importe</th>
              {showPonderado && <th className="text-center p-2.5">Prob.</th>}
              {showPonderado && <th className="text-right p-2.5">Ponderado</th>}
              <th className="text-left p-2.5">Presentación</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {forecastRows.length === 0 && (
              <tr><td colSpan={showPonderado ? 7 : 5} className="text-center py-6 text-gray-400" data-testid="t360-forecast-empty">Sin oportunidades ≥ {minMm} M€.</td></tr>
            )}
            {forecastRows.map(it => (
                <tr key={it.id} onClick={() => navigate(`/ofertas-comercial?tab=list&open=${it.id}`)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="p-2.5 text-gray-500">{it.stage}</td>
                  <td className="p-2.5 font-semibold text-gray-900">{it.municipio}</td>
                  <td className="p-2.5 text-gray-700 max-w-xs truncate">{it.titulo}</td>
                  <td className="p-2.5 text-right font-mono">{fmtEur(it.importe)}</td>
                  {showPonderado && (
                  <td className="p-2.5 text-center">
                    {it.probabilidad}%
                    {(it.probabilidad_origen === "sin_revisar" || it.probabilidad_origen === "sin_estimar") && (
                      <span className="ml-1 text-[9px] text-amber-500 font-semibold" title="Sin estimación confirmada por un analista; hipótesis por etapa">hipótesis</span>
                    )}
                  </td>
                  )}
                  {showPonderado && <td className="p-2.5 text-right font-mono text-[#007934] font-semibold">{fmtEur(it.importe_ponderado)}</td>}
                  <td className="p-2.5 text-gray-500">
                    {fmtFechaOferta(it.fecha_presentacion)}
                    {it.sin_horizonte && (
                      <span className="ml-1 text-[9px] text-gray-400 font-semibold" title="Sin fecha de adjudicación estimada; no tiene horizonte de cierre definido">sin horizonte</span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* T14 · Previsiones vencidas — fuera del forecast vigente, visibles para revisión */}
      {previsiones_vencidas?.count > 0 && (
        <div className="bg-white rounded-xl border border-rose-200 overflow-hidden" data-testid="pipeline-vencidas-section">
          <div className="p-3 border-b border-rose-100 bg-rose-50/60">
            <span className="text-xs font-semibold text-rose-700 uppercase tracking-wide">Previsiones vencidas — revisar ({previsiones_vencidas.count})</span>
            <p className="text-[11px] text-rose-600 mt-0.5">{previsiones_vencidas.nota}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] tracking-wide">
                <tr>
                  <th className="text-left p-2.5">Municipio</th>
                  <th className="text-left p-2.5">Licitación</th>
                  <th className="text-right p-2.5">Importe</th>
                  <th className="text-left p-2.5">Fecha estimada (vencida)</th>
                  <th className="text-left p-2.5">Responsable</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {previsiones_vencidas.items.map(it => (
                  <tr key={it.id} onClick={() => navigate(`/ofertas-comercial?tab=list&open=${it.id}`)} className="hover:bg-rose-50 cursor-pointer" data-testid={`pipeline-vencida-row-${it.id}`}>
                    <td className="p-2.5 font-semibold text-gray-900">{it.municipio}</td>
                    <td className="p-2.5 text-gray-700 max-w-xs truncate">{it.titulo}</td>
                    <td className="p-2.5 text-right font-mono">{fmtEur(it.importe)}</td>
                    <td className="p-2.5 text-rose-600 font-semibold">{fmtFechaOferta(it.fecha_adjudicacion_estimada)}</td>
                    <td className="p-2.5 text-gray-500">{it.responsable || "Sin asignar"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Entrega B · Cola de conciliación — expedientes solo en el kanban legacy */}
      {reconciliacion?.count > 0 && (
        <div className="bg-white rounded-xl border border-indigo-200 overflow-hidden" data-testid="pipeline-reconciliacion-section">
          <div className="p-3 border-b border-indigo-100 bg-indigo-50/60 flex items-center justify-between flex-wrap gap-2">
            <div>
              <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide flex items-center gap-1.5">
                <ClipboardCheck size={13} /> Pendiente de cuadrar ({reconciliacion.count})
              </span>
              <p className="text-[11px] text-indigo-600/90 mt-0.5 max-w-3xl">{reconciliacion.nota}</p>
            </div>
            <span className="text-[11px] font-mono text-indigo-700 font-semibold">{fmtEur(reconciliacion.importe_eur)}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] tracking-wide">
                <tr>
                  <th className="text-left p-2.5">Expediente</th>
                  <th className="text-left p-2.5">Municipio</th>
                  <th className="text-left p-2.5">Licitación</th>
                  <th className="text-right p-2.5">Importe</th>
                  <th className="text-left p-2.5">Estado (legacy)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reconciliacion.items.map(it => (
                  <tr key={it.id} onClick={() => navigate(`/licitaciones/${it.id}`)} className="hover:bg-indigo-50 cursor-pointer" data-testid={`pipeline-recon-row-${it.id}`}>
                    <td className="p-2.5 font-mono text-gray-500">{it.expediente || "—"}</td>
                    <td className="p-2.5 font-semibold text-gray-900">{it.municipio || "—"}</td>
                    <td className="p-2.5 text-gray-700 max-w-xs truncate">{it.titulo}</td>
                    <td className="p-2.5 text-right font-mono">{fmtEur(it.importe)}</td>
                    <td className="p-2.5 text-gray-500">{it.estado_crm || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Entrega B · Cartera operativa — MISMA fuente `ofertas_comercial` que el
          pipeline de arriba (ya no hay fuentes separadas). Es la vista por
          ESTADO/FASE (determinista), complementaria al forecast ponderado. */}
      {cartera && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="cartera-operativa-section">
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              Cartera operativa
              <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[9px] font-semibold normal-case tracking-normal">Vista por estado/fase</span>
            </h3>
            <p className="text-[10px] text-gray-400 mt-0.5">{cartera.fuente}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-lg border border-gray-100 p-3" data-testid="cartera-pipeline-abierto">
              <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide">Pipeline abierto (bruto)</p>
              <p className="text-lg font-bold text-gray-900 mt-1">{fmtEur(cartera.pipeline_abierto.importe_mm * 1_000_000)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{cartera.pipeline_abierto.count} oportunidades · {cartera.pipeline_abierto.nota}</p>
            </div>
            <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-3" data-testid="cartera-adjudicado-no-firmado">
              <p className="text-[10px] text-amber-600 uppercase font-semibold tracking-wide">Adjudicado no firmado</p>
              <p className="text-lg font-bold text-amber-700 mt-1">{fmtEur(cartera.adjudicado_no_firmado.value_mm * 1_000_000)}</p>
              <p className="text-[10px] text-amber-600/80 mt-0.5">{cartera.adjudicado_no_firmado.count} contratos · {cartera.adjudicado_no_firmado.nota}</p>
            </div>
            <div className="rounded-lg border border-[#007934]/20 bg-[#f0fdf4] p-3" data-testid="cartera-firme">
              <p className="text-[10px] text-[#007934] uppercase font-semibold tracking-wide">Cartera firme</p>
              <p className="text-lg font-bold text-[#007934] mt-1">{fmtEur(cartera.cartera_firme.value_mm * 1_000_000)}</p>
              <p className="text-[10px] text-[#007934]/70 mt-0.5">{cartera.cartera_firme.count} contratos · {cartera.cartera_firme.nota}</p>
            </div>
          </div>
          <p className="text-[10px] text-gray-400 italic">No incluye &quot;ponderado&quot; propio: esta fuente clasifica por estado/fase, no guarda una probabilidad por registro — forzar un % aquí sería un dato inventado.</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// M8: Viability — Go/No-Go Pando
// ═══════════════════════════════════════════════════════════════════════════
